import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

// Modal Styles
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: ${theme.spacing.xxl};
  max-width: 700px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid ${theme.colors.primaryLight}20;
  position: relative;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg};
    margin: 16px;
    width: calc(100% - 32px);
    max-height: calc(100vh - 32px);
    border-radius: 20px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.md};
  position: relative;
  width: 100%;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50%;
    height: 2px;
    background: #f0f0f0;
  }
`;

const ModalTitle = styled.h2`
  color: ${theme.colors.deepDark};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${theme.colors.lightText};
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-height: ${theme.mobile.minTouchTarget};
  min-width: ${theme.mobile.minTouchTarget};
  
  &:hover {
    color: ${theme.colors.deepDark};
    background: ${theme.colors.primaryLight}20;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const StepContainer = styled.div`
  min-height: 350px;
  display: flex;
  flex-direction: column;
`;

const StepTitle = styled.h3`
  color: ${theme.colors.deepDark};
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const StepDescription = styled.p`
  color: ${theme.colors.lightText};
  margin-bottom: ${theme.spacing.xl};
  line-height: 1.5;
  font-size: 0.9rem;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${theme.colors.deepDark};
  margin-bottom: ${theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${theme.colors.primaryLight}10;
  border-radius: 12px;
  font-size: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}10;
  }
`;

const MemberRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  margin-bottom: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  background: ${theme.colors.background};
  border-radius: 12px;
  border: 1px solid ${theme.colors.primaryLight}10;
`;

const MemberInput = styled(Input)`
  margin-bottom: 0;
`;

const AgeInput = styled(MemberInput)`
  width: 80px;
  flex-shrink: 0;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.error};
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-size: 18px;
  font-weight: bold;
  
  &:hover {
    background: ${theme.colors.error}20;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  color: ${theme.colors.primary};
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  font-size: 28px;
  font-weight: 300;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0 auto;
  line-height: 1;
  
  &:hover:not(:disabled) {
    color: ${theme.colors.secondary};
    transform: scale(1.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }
`;

const ColorPickerContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
  padding: 4px;
  background: ${theme.colors.background};
  border-radius: 8px;
  border: 1px solid ${theme.colors.primaryLight}10;
`;

const ColorOption = styled.button<{ color: string; isSelected: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 100%;
  background: ${props => props.color};
  border: 0.1px solid ${props => props.isSelected ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.2);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: scale(1.0);
  }
`;

const MemberColorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const ColorPreview = styled.div<{ color?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color || theme.colors.primaryLight};
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const RoomTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const RoomTag = styled.button<{ isSelected: boolean; count?: number }>`
  display: inline-flex;
  align-items: center;
  background: ${props => props.isSelected ? theme.colors.primary : 'white'};
  color: ${props => props.isSelected ? 'white' : theme.colors.deepDark};
  border: 2px solid ${props => props.isSelected ? theme.colors.primary : `${theme.colors.primaryLight}60`};
  border-radius: 20px;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 6px;
  
  &:hover {
    background: ${props => props.isSelected ? theme.colors.secondary : `${theme.colors.primaryLight}20`};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CountBadge = styled.span`
  background: rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 2px 6px;
  font-size: 12px;
  font-weight: 600;
`;

const TimeSlotContainer = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const MemberTimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  background: ${theme.colors.background};
  border-radius: 12px;
  border: 1px solid ${theme.colors.primaryLight}30;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.sm};
  }
`;

const MemberName = styled.span`
  font-weight: 500;
  color: ${theme.colors.deepDark};
  min-width: 100px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    min-width: auto;
  }
`;

const TimeInput = styled(Input)`
  width: 120px;
  margin-bottom: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing.xxl};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.primaryLight}30;
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StepDot = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => 
    props.isCompleted ? theme.colors.primary : 
    props.isActive ? theme.colors.secondary : 
    `${theme.colors.primaryLight}40`};
  transition: all 0.2s ease;
`;

const NavButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' ? theme.colors.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : theme.colors.primary};
  border: 2px solid ${theme.colors.primary};
  border-radius: 12px;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
  min-height: ${theme.mobile.minTouchTarget};
  
  &:hover {
    background: ${props => props.variant === 'primary' ? theme.colors.secondary : `${theme.colors.primary}10`};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const MemberTag = styled.div<{ color?: string }>`
  display: inline-flex;
  align-items: center;
  background: ${props => props.color || theme.colors.primaryLight};
  color: white;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  gap: ${theme.spacing.xs};
  margin: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const MemberTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.md};
  min-height: 40px;
  align-items: flex-start;
`;

const NewMemberForm = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  background: ${theme.colors.background};
  border-radius: 12px;
  border: 1px solid ${theme.colors.primaryLight}30;
`;

const MemberTagRemoveButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 0;
  margin-left: ${theme.spacing.xs};
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: bold;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

interface HouseholdMember {
  name: string;
  age: number;
}

interface RoomData {
  [key: string]: number;
}

interface HomeTime {
  member: string;
  homeFrom: string;
  homeTo: string;
}

interface NestTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    householdMembers: HouseholdMember[];
    rooms: RoomData;
    homeTimes: HomeTime[];
    memberColors: { [key: number]: string };
  }) => void;
  initialHouseholdMembers?: HouseholdMember[];
}

// Color options for household member tags
const colorOptions = [
  '#E53E3E', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Sky Blue
  '#96CEB4', // Sage Green
  '#515a7a', // Dark Purple
  '#2C5282', // Navy
  '#FF8B94', // Pink
  '#B4A7D6', // Lavender
  '#F7DC6F', // Golden Yellow
  '#85C1E9', // Light Blue
  '#F8C471', // Peach
  '#82E0AA', // Light Green
];

const NestTestModal: React.FC<NestTestModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialHouseholdMembers = []
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMember[]>(initialHouseholdMembers);
  const [rooms, setRooms] = useState<RoomData>({});
  const [homeTimes, setHomeTimes] = useState<HomeTime[]>([]);
  const [memberColors, setMemberColors] = useState<{ [key: number]: string }>({});
  const [selectedMemberForColor, setSelectedMemberForColor] = useState<number | null>(null);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberAge, setNewMemberAge] = useState<number | ''>('');

  const roomOptions = [
    'Living Room', 'Kitchen', 'Bedroom', 'Bathroom', 'Dining Room',
    'Office', 'Study', 'Playroom', 'Garage', 'Basement', 'Attic',
    'Laundry Room', 'Guest Room', 'Balcony', 'Garden'
  ];

  useEffect(() => {
    if (isOpen && initialHouseholdMembers.length > 0) {
      setHouseholdMembers(initialHouseholdMembers);
      // Initialize home times for existing members
      const initialTimes = initialHouseholdMembers.map(member => ({
        member: member.name,
        homeFrom: '',
        homeTo: ''
      }));
      setHomeTimes(initialTimes);
    }
  }, [isOpen, initialHouseholdMembers]);

  const addHouseholdMember = () => {
    if (newMemberName.trim() && newMemberAge) {
      const newMember = { name: newMemberName.trim(), age: Number(newMemberAge) };
      const newIndex = householdMembers.length;
      
      setHouseholdMembers([...householdMembers, newMember]);
      setHomeTimes([...homeTimes, { member: newMemberName.trim(), homeFrom: '', homeTo: '' }]);
      
      // Assign a random color from available options
      const availableColors = colorOptions.filter(color => 
        !Object.values(memberColors).includes(color)
      );
      const randomColor = availableColors.length > 0 
        ? availableColors[Math.floor(Math.random() * availableColors.length)]
        : colorOptions[newIndex % colorOptions.length];
      
      setMemberColors(prev => ({
        ...prev,
        [newIndex]: randomColor
      }));
      
      // Clear the form
      setNewMemberName('');
      setNewMemberAge('');
    }
  };

  const removeHouseholdMember = (index: number) => {
    const updatedMembers = householdMembers.filter((_, i) => i !== index);
    setHouseholdMembers(updatedMembers);
    setHomeTimes(homeTimes.filter((_, i) => i !== index));
    
    // Remove color for this member and shift colors for remaining members
    const updatedColors = { ...memberColors };
    delete updatedColors[index];
    
    // Shift colors for members after the removed one
    const newColors: { [key: number]: string } = {};
    Object.keys(updatedColors).forEach(key => {
      const keyNum = parseInt(key);
      if (keyNum > index) {
        newColors[keyNum - 1] = updatedColors[keyNum];
      } else {
        newColors[keyNum] = updatedColors[keyNum];
      }
    });
    setMemberColors(newColors);
    
    // Close color picker if it was open for this member
    if (selectedMemberForColor === index) {
      setSelectedMemberForColor(null);
    } else if (selectedMemberForColor !== null && selectedMemberForColor > index) {
      setSelectedMemberForColor(selectedMemberForColor - 1);
    }
  };

  const updateHouseholdMember = (index: number, field: 'name' | 'age', value: string | number) => {
    const updated = [...householdMembers];
    updated[index] = { ...updated[index], [field]: value };
    setHouseholdMembers(updated);
    
    // Update corresponding home time member name
    if (field === 'name') {
      const updatedTimes = [...homeTimes];
      updatedTimes[index] = { ...updatedTimes[index], member: value as string };
      setHomeTimes(updatedTimes);
    }
  };

  const handleColorSelect = (memberIndex: number, color: string) => {
    setMemberColors(prev => ({
      ...prev,
      [memberIndex]: color
    }));
    setSelectedMemberForColor(null);
  };

  const toggleColorPicker = (memberIndex: number) => {
    setSelectedMemberForColor(selectedMemberForColor === memberIndex ? null : memberIndex);
  };

  // Handle click outside to close color picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (selectedMemberForColor !== null && 
          !target.closest('[data-color-picker]') && 
          !target.closest('[data-color-preview]')) {
        setSelectedMemberForColor(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedMemberForColor]);

  const toggleRoom = (room: string) => {
    setRooms(prev => ({
      ...prev,
      [room]: (prev[room] || 0) + 1
    }));
  };

  const removeRoom = (room: string) => {
    setRooms(prev => {
      const updated = { ...prev };
      if (updated[room] > 1) {
        updated[room]--;
      } else {
        delete updated[room];
      }
      return updated;
    });
  };

  const updateHomeTime = (index: number, field: 'homeFrom' | 'homeTo', value: string) => {
    const updated = [...homeTimes];
    updated[index] = { ...updated[index], [field]: value };
    setHomeTimes(updated);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return householdMembers.length > 0 && householdMembers.every(m => m.name.trim() !== '');
      case 1:
        return Object.keys(rooms).length > 0;
      case 2:
        return true; // Home times are optional
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Add bedroom for each household member
    const updatedRooms = { ...rooms };
    householdMembers.forEach(member => {
      const bedroomKey = `${member.name}'s Bedroom`;
      updatedRooms[bedroomKey] = 1;
    });

    onSubmit({
      householdMembers,
      rooms: updatedRooms,
      homeTimes,
      memberColors
    });
    onClose();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepContainer>
            <StepTitle>Who makes up your nest at home?</StepTitle>
            <StepDescription>
              Add the people who live in your household. This will help us create personalized plans and assign tasks.
            </StepDescription>
            
            {/* Display existing members as tags */}
            <MemberTagsContainer>
              {householdMembers.map((member, index) => (
                <div key={index}>
                  <MemberTag 
                    color={memberColors[index]} 
                    onClick={() => toggleColorPicker(index)}
                    title="Click to change color"
                  >
                    {member.name} ({member.age})
                    <MemberTagRemoveButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        removeHouseholdMember(index);
                      }}
                    >
                      ×
                    </MemberTagRemoveButton>
                  </MemberTag>
                  {selectedMemberForColor === index && (
                    <ColorPickerContainer data-color-picker>
                      {colorOptions.map((color, colorIndex) => (
                        <ColorOption
                          key={colorIndex}
                          color={color}
                          isSelected={memberColors[index] === color}
                          onClick={() => handleColorSelect(index, color)}
                          title={`Select color`}
                        />
                      ))}
                    </ColorPickerContainer>
                  )}
                </div>
              ))}
            </MemberTagsContainer>
            
            {/* Form for adding new members */}
            <NewMemberForm>
              <MemberInput
                placeholder="Name"
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addHouseholdMember();
                  }
                }}
              />
              <AgeInput
                type="number"
                placeholder="Age"
                value={newMemberAge}
                onChange={(e) => setNewMemberAge(e.target.value ? parseInt(e.target.value) : '')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addHouseholdMember();
                  }
                }}
              />
              <AddButton 
                onClick={addHouseholdMember} 
                title="Add household member"
                disabled={!newMemberName.trim() || !newMemberAge}
              >
                +
              </AddButton>
            </NewMemberForm>
          </StepContainer>
        );

      case 1:
        return (
          <StepContainer>
            <StepTitle>What kind of rooms do you have at home?</StepTitle>
            <StepDescription>
              Select the rooms in your home. Click multiple times if you have more than one of the same type.
              We'll automatically add a bedroom for each household member.
            </StepDescription>
            
            <RoomTagsContainer>
              {roomOptions.map(room => (
                <RoomTag
                  key={room}
                  isSelected={rooms[room] > 0}
                  onClick={() => toggleRoom(room)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    removeRoom(room);
                  }}
                >
                  {room}
                  {rooms[room] > 1 && <CountBadge>{rooms[room]}</CountBadge>}
                </RoomTag>
              ))}
            </RoomTagsContainer>
            
            {householdMembers.length > 0 && householdMembers.some(m => m.name.trim() !== '') && (
              <>
                <div style={{ 
                  marginTop: '16px', 
                  marginBottom: '12px', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: theme.colors.deepDark 
                }}>
                  Auto-generated bedrooms:
                </div>
                <RoomTagsContainer>
                  {householdMembers
                    .filter(member => member.name.trim() !== '')
                    .map((member, index) => (
                      <RoomTag
                        key={`bedroom-${index}`}
                        isSelected={true}
                        style={{ 
                          opacity: 0.7,
                          cursor: 'default',
                          background: memberColors[index] || theme.colors.primaryLight + '60',
                          border: 'none'
                        }}
                      >
                        {member.name}'s Bedroom
                      </RoomTag>
                    ))}
                </RoomTagsContainer>
              </>
            )}
            
            <p style={{ fontSize: '12px', color: theme.colors.lightText, fontStyle: 'italic' }}>
              Tip: Right-click to remove a room
            </p>
          </StepContainer>
        );

      case 2:
        return (
          <StepContainer>
            <StepTitle>Home times</StepTitle>
            <StepDescription>
              When is each household member typically home? This helps us schedule tasks and activities.
            </StepDescription>
            
            {householdMembers.map((member, index) => (
              <MemberTimeRow key={index}>
                <MemberName>{member.name}</MemberName>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', color: theme.colors.lightText }}>From:</span>
                  <TimeInput
                    type="time"
                    value={homeTimes[index]?.homeFrom || ''}
                    onChange={(e) => updateHomeTime(index, 'homeFrom', e.target.value)}
                  />
                  <span style={{ fontSize: '14px', color: theme.colors.lightText }}>To:</span>
                  <TimeInput
                    type="time"
                    value={homeTimes[index]?.homeTo || ''}
                    onChange={(e) => updateHomeTime(index, 'homeTo', e.target.value)}
                  />
                </div>
              </MemberTimeRow>
            ))}
            
            <p style={{ fontSize: '12px', color: theme.colors.lightText, fontStyle: 'italic' }}>
              Leave empty if someone is home all day or has irregular schedules
            </p>
          </StepContainer>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
            The Nest Test
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        {renderStep()}

        <ButtonContainer>
          <NavButton 
            onClick={handlePrevious} 
            disabled={currentStep === 0}
          >
            Previous
          </NavButton>
          
          <StepIndicator>
            {[0, 1, 2].map(step => (
              <StepDot
                key={step}
                isActive={step === currentStep}
                isCompleted={step < currentStep}
              />
            ))}
          </StepIndicator>
          
          {currentStep < 2 ? (
            <NavButton 
              variant="primary" 
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </NavButton>
          ) : (
            <NavButton 
              variant="primary" 
              onClick={handleSubmit}
            >
              Complete Test
            </NavButton>
          )}
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default NestTestModal; 